<template>
  <div class="blog-detail">
    <div class="blog-box">
      <el-row type="flex" justify="space-between">
        <el-col :lg="16" :md="24">
          <el-skeleton :loading="isLoading" :rows="15" animated>
            <h1>{{blog.title}}</h1>
            <section class="profile-box">
              <el-space :size="10" spacer="|">
                <span class="catalog">
                  <el-tag size="small"><i class="el-icon-folder" /> {{blog.category.categoryName}}</el-tag>
                </span>
                <span class="tags">
                  <el-tag :key="t.id" v-for="t in blog.tags" size="small" class="tag" :type="randomTagType()">
                    <i class="el-icon-collection-tag" /><span> {{t.tagName}}</span>
                  </el-tag>
                </span>
                <span class="time">
                  <span>{{blog.gmtCreate}}</span>
                </span>
              </el-space>
            </section>
            <section class="markdown-box">
              <MarkdownShow :content="blog.markdownText" />
            </section>
            <section class="tools-box">
              <el-button class="tool-btn" :plain="!isLiked" type="primary" icon="el-icon-sunny" @click="like">喜欢 ({{blog.likeCount || 0}})</el-button>
              <el-button class="tool-btn" plain type="danger" icon="el-icon-money" @click="appreciate">赞赏</el-button>
            </section>
            <section class="author-box">
              <h4>原文信息</h4>
              <p>作者: 涂小庆</p>
              <p>文章链接: <a :href="currentFullPath">{{currentFullPath}}</a></p>
              <p>版权声明：本博客所有文章除特别声明外, 转载请注明出处!</p>
            </section>
            <section class="comment-box">
              <el-popover placement="left" title="温馨提示" :width="200" trigger="hover" content="由于网站审核严格, 评论功能暂时关闭, 带来不便, 敬请谅解." :disabled="!commentDisabled">
                <template #reference>
                  <div class="textarea-box">
                    <el-input type="textarea" placeholder="请输入评论内容" v-model="commentText" :autosize="{minRows:2,maxRows:20}" :disabled="commentDisabled">
                    </el-input>
                  </div>
                </template>
              </el-popover>
              <div class="submit-box">
                <span>
                  <el-button class="tool-btn" size="small" type="primary" icon="el-icon-chat-square" :disabled="commentDisabled">评论</el-button>
                </span>
              </div>
            </section>
          </el-skeleton>
        </el-col>
        <el-col :lg="7" class="hidden-md-and-down">
          <Aside />
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script lang="ts">
import Aside from '@/components/Aside.vue'
import MarkdownShow from '@/components/MarkdownShow.vue'
import { useGetRequest, useLog } from '@/hooks'
import { AssertGoodResponse, getRandomFromArr } from '@/util/GlobalUtil'
import { ElMessage } from 'element-plus'
import { defineComponent, reactive, ref, toRefs } from 'vue'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: {
    Aside,
    MarkdownShow,
  },
  setup() {
    // eslint-disable-next-line
    const l = useLog()
    const commentDisabled = ref<boolean>(true)
    const markdownText = ref<string>(
      '## Linux学习笔记 \n ### 常用命令:' +
        `

      命令 | 描述
      ---|---
      sync | 保存文件
      shutdown | 关机
      shutdown -h 10 | 十分钟后关机
      shutdown -h now 或 halt | 立即关机
      shutdown -r now 或 reboot | 立即重启
      su | 切换到超级管理员
      mkdir *** | 创建目录
      rmdir *** | 删除目录
    ` +
        '\n\n\n\n![avatar](https://dss0.bdstatic.com/70cFvHSh_Q1YnxGkpoWK1HF6hhy/it/u=233301930,3031623456&fm=11&gp=0.jpg)'
    )
    const state = reactive({
      commentText: '',
      isLiked: false,
    })

    // 加载博客详情
    const isLoading = ref<boolean>(true)
    const { params } = useRoute()
    // eslint-disable-next-line
    const blog = ref<any>({})

    // 浏览量+1
    useGetRequest(`/api/blog/add_view/${params.blogId}`).then((res) => {
      AssertGoodResponse(res)
      ElMessage.success('浏览量+1')
    })

    // 调接口获取博客详情数据
    useGetRequest(`/api/blog/${params.blogId}`)
      .then((res) => {
        AssertGoodResponse(res)
        blog.value = res.data.data
      })
      .finally(() => {
        isLoading.value = false
      })
    // 随机tag类型
    const randomTagType = () =>
      getRandomFromArr(['info', '', 'danger', 'warning', 'success'])
    // 喜欢按钮
    const like = () => {
      if (state.isLiked) {
        ElMessage.warning('你已经点过赞了哦.')
        return
      }
      // 调点赞接口
      useGetRequest(`/api/blog/like/${params.blogId}`)
        .then((res) => {
          AssertGoodResponse(res)
          state.isLiked = true
          blog.value.likeCount++
          ElMessage.success('点赞成功!')
        })
        .catch((err) => {
          ElMessage.error('点赞失败,' + err)
        })
    }
    // 赞赏按钮
    const appreciate = () => {
      ElMessage.success('感谢你的欣赏, 请扫右边二维哦.')
    }

    return {
      ...toRefs(state),
      isLoading,
      commentDisabled,
      markdownText,
      randomTagType,
      blog,
      currentFullPath: window.location.href,
      appreciate,
      like,
    }
  },
})
</script>


<style lang="scss" scoped>
.blog-detail {
  .blog-box {
    margin-top: 30px;

    .profile-box {
      margin-top: 20px;
      color: #909399;
      .tags {
        .tag:not(:first-child) {
          margin-left: 10px;
        }
      }
      .time {
        color: $gray-font;
        font-size: 0.9rem;
      }
    }

    .markdown-box {
      // border: 1px solid green;
      padding-top: 30px;
    }

    .tools-box {
      // border: 1px solid blue;
      padding: 50px 0 0 0;
      text-align: center;

      .tool-btn + .tool-btn {
        margin-left: 30px;
      }
    }

    .author-box {
      margin: 50px 0 0 0;
      // border: 1px solid orange;
      padding: 15px;
      border-left: 5px solid $theme-color;
      background: #f7f7f7;
      line-height: 1.5rem;
      > p {
        font-size: 0.96rem;
      }
      a {
        text-decoration: none;
        color: $gray-font;
        transition: color 0.3s;
        &:hover {
          color: $theme-color;
        }
      }
    }

    .comment-box {
      // border: 1px solid red;
      padding: 50px 0;
      .submit-box {
        padding-top: 5px;
        text-align: right;
      }
    }
  }
}
</style>